import { SettingsElementType, } from '@thoughtspot/ts-chart-sdk';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { sanitizeCustomVisualProps } from '../form-builder.util';
import { isPropElement } from './custom-chart-schema-hydrator.util';
import { STORED_VISUAL_PROP_NAMESPACES } from './store-key-namespaces';
const validateClientState = (clientState) => {
    if (typeof clientState !== 'string') {
        return '';
    }
    try {
        JSON.parse(clientState);
        return clientState;
    }
    catch {
        return '';
    }
};
const SINGLE_VALUE_ELEMENTS = [
    SettingsElementType.RADIO_GROUP,
];
export { STORED_VISUAL_PROP_NAMESPACES };
const CONTAINER_ELEMENT_TYPES = new Set([
    SettingsElementType.SECTION,
    SettingsElementType.ACCORDION,
    SettingsElementType.ACCORDION_ITEM,
]);
export const getHonoredStoreKey = (element) => {
    if (!element.storeKey ||
        !element.key ||
        element.isAnswerProperty ||
        element.children?.length ||
        CONTAINER_ELEMENT_TYPES.has(element.elementType)) {
        return null;
    }
    const [storedNamespace, ...rest] = element.storeKey.split('.');
    return STORED_VISUAL_PROP_NAMESPACES.has(storedNamespace) &&
        rest.length &&
        rest.every(segment => segment.length > 0)
        ? element.storeKey
        : null;
};
export const getElementValue = (element) => {
    if (isPropElement(element)) {
        const elementType = element.type;
        if (elementType !== 'section' && elementType !== 'customsort') {
            return element?.defaultValue;
        }
        return '';
    }
    switch (element.elementType) {
        case SettingsElementType.RADIO_GROUP:
            return (element?.properties?.selectedValue ??
                element?.properties?.defaultValue);
        case SettingsElementType.DROPDOWN:
            return element?.properties?.selectedOptions;
        case SettingsElementType.COLOR_PICKER:
            return element?.properties?.color ?? element?.properties?.value;
        case SettingsElementType.TOGGLE:
        case SettingsElementType.RADIO:
            return element?.properties?.checked;
        default:
            return element?.properties?.value;
    }
};
export const sanitizeSettingsElementsArray = (currentVisualProps, elements, globalPersistedVisualPropKeys, preferLowercaseKeys = false, isCustomChart = false, storeKeyContext) => {
    const visualProps = {};
    const processElement = (element) => {
        if (element?.children &&
            element?.children.length &&
            !SINGLE_VALUE_ELEMENTS.includes(element.elementType)) {
            if (element.key && !element.isAnswerProperty) {
                const divertedBefore = storeKeyContext?.storeKeyWrites.length ?? 0;
                const nestedResult = sanitizeSettingsElementsArray(currentVisualProps?.[element.key], element.children, globalPersistedVisualPropKeys, preferLowercaseKeys, isCustomChart, storeKeyContext);
                const existingValue = _.get(visualProps, element.key);
                const ownValue = getElementValue(element) || {};
                let mergedValue = nestedResult;
                if (existingValue &&
                    _.isObject(existingValue) &&
                    _.isObject(nestedResult)) {
                    mergedValue = { ...existingValue, ...nestedResult };
                }
                if (_.isObject(ownValue)) {
                    mergedValue = { ...mergedValue, ...ownValue };
                }
                const descendantsDiverted = (storeKeyContext?.storeKeyWrites.length ?? 0) >
                    divertedBefore;
                if (!_.isEmpty(mergedValue) || !descendantsDiverted) {
                    _.set(visualProps, element.key, mergedValue);
                }
            }
            else {
                element.children.forEach(processElement);
            }
        }
        else if (element.key && !element.isAnswerProperty) {
            const defaultValue = getElementValue(element);
            const pinnedStoreKey = storeKeyContext
                ? getHonoredStoreKey(element)
                : null;
            const currentValue = pinnedStoreKey
                ? _.get(storeKeyContext?.storedRoot, pinnedStoreKey)
                : currentVisualProps?.[element.key];
            let resolvedValue = currentValue;
            if (preferLowercaseKeys && !pinnedStoreKey) {
                const lowerKey = element.key.toLowerCase();
                const lowerValue = lowerKey !== element.key
                    ? currentVisualProps?.[lowerKey]
                    : undefined;
                if (lowerValue !== undefined && lowerValue !== null) {
                    resolvedValue = lowerValue;
                }
            }
            const shouldUseDefault = resolvedValue === undefined ||
                resolvedValue === null ||
                (_.isObject(resolvedValue) && _.isEmpty(resolvedValue));
            const finalValue = shouldUseDefault ? defaultValue : resolvedValue;
            if (pinnedStoreKey && storeKeyContext) {
                storeKeyContext.storeKeyWrites.push({
                    path: pinnedStoreKey,
                    value: finalValue,
                });
            }
            else {
                _.set(visualProps, element.key, finalValue);
            }
        }
    };
    elements.forEach(processElement);
    const currentVisualPropsWithPreservedState = currentVisualProps?.clientState
        ? {
            ...visualProps,
            clientState: isCustomChart
                ? DOMPurify.sanitize(currentVisualProps.clientState)
                : validateClientState(currentVisualProps.clientState),
        }
        : visualProps;
    if (!globalPersistedVisualPropKeys ||
        _.isEmpty(globalPersistedVisualPropKeys)) {
        return currentVisualPropsWithPreservedState;
    }
    globalPersistedVisualPropKeys.forEach(value => {
        if (_.get(currentVisualProps, value)) {
            _.set(currentVisualPropsWithPreservedState, value, DOMPurify.sanitize(_.get(currentVisualProps, value)));
        }
    });
    return currentVisualPropsWithPreservedState;
};
export const sanitizeCustomVisualPropsV2 = (customVisualProps, visualPropEditorDefinition, globalPersistedVisualPropKeys, isCustomChart = false) => {
    let sanitizedVisualProps = {
        columnVisualProps: {},
        axisVisualProps: {},
        dataLabelVisualProps: {},
        tooltipVisualProps: {},
        legendVisualProps: {},
        displayVisualProps: {},
    };
    const { elements, columnsVizPropDefinition, displayVizPropDefinition, dataLabelVizPropDefinition, tooltipVizPropDefinition, legendVizPropDefinition, axisVizPropDefinition, } = visualPropEditorDefinition;
    const isMigrated = customVisualProps?.isMigrated === true;
    const storeKeyWrites = [];
    const storeKeyContext = {
        storedRoot: customVisualProps ?? {},
        storeKeyWrites,
    };
    if (columnsVizPropDefinition && columnsVizPropDefinition.length) {
        sanitizedVisualProps.columnVisualProps = {};
        columnsVizPropDefinition.forEach(item => {
            Object.entries(item.columnSettingsDefinition).forEach(([colId, colDef]) => {
                const originalColumnProps = customVisualProps?.columnVisualProps?.[colId] ?? {};
                sanitizedVisualProps.columnVisualProps[colId] = sanitizeSettingsElementsArray(originalColumnProps, (colDef?.elements || []), globalPersistedVisualPropKeys, isMigrated, isCustomChart, storeKeyContext);
                if (originalColumnProps?.conditionalFormatting) {
                    sanitizedVisualProps.columnVisualProps[colId].conditionalFormatting =
                        originalColumnProps.conditionalFormatting;
                }
            });
        });
    }
    if (axisVizPropDefinition && axisVizPropDefinition.length) {
        sanitizedVisualProps.axisVisualProps = sanitizeSettingsElementsArray(customVisualProps?.axisVisualProps ?? {}, axisVizPropDefinition, globalPersistedVisualPropKeys, false, isCustomChart, storeKeyContext);
    }
    if (dataLabelVizPropDefinition && dataLabelVizPropDefinition.length) {
        sanitizedVisualProps.dataLabelVisualProps = sanitizeSettingsElementsArray(customVisualProps?.dataLabelVisualProps ?? {}, dataLabelVizPropDefinition, globalPersistedVisualPropKeys, false, isCustomChart, storeKeyContext);
    }
    if (tooltipVizPropDefinition && tooltipVizPropDefinition.length) {
        sanitizedVisualProps.tooltipVisualProps = sanitizeSettingsElementsArray(customVisualProps?.tooltipVisualProps ?? {}, tooltipVizPropDefinition, globalPersistedVisualPropKeys, false, isCustomChart, storeKeyContext);
    }
    if (legendVizPropDefinition && legendVizPropDefinition.length) {
        sanitizedVisualProps.legendVisualProps = sanitizeSettingsElementsArray(customVisualProps?.legendVisualProps ?? {}, legendVizPropDefinition, globalPersistedVisualPropKeys, false, isCustomChart, storeKeyContext);
    }
    if (displayVizPropDefinition && displayVizPropDefinition.length) {
        sanitizedVisualProps.displayVisualProps = sanitizeSettingsElementsArray(customVisualProps?.displayVisualProps ?? {}, displayVizPropDefinition, globalPersistedVisualPropKeys, false, isCustomChart, storeKeyContext);
    }
    storeKeyWrites.forEach(({ path, value }) => {
        _.set(sanitizedVisualProps, path, value);
    });
    if (customVisualProps?.clientState) {
        sanitizedVisualProps.clientState = isCustomChart
            ? DOMPurify.sanitize(customVisualProps.clientState)
            : validateClientState(customVisualProps.clientState);
    }
    if (elements && elements.length) {
        sanitizedVisualProps = {
            ...sanitizedVisualProps,
            ...sanitizeCustomVisualProps(customVisualProps, { elements }, globalPersistedVisualPropKeys),
        };
    }
    if (globalPersistedVisualPropKeys &&
        !_.isEmpty(globalPersistedVisualPropKeys)) {
        globalPersistedVisualPropKeys.forEach(key => {
            const value = _.get(customVisualProps, key);
            if (value !== undefined) {
                _.set(sanitizedVisualProps, key, DOMPurify.sanitize(value));
            }
        });
    }
    if (typeof customVisualProps?.chartSettingsVersion === 'string') {
        sanitizedVisualProps.chartSettingsVersion =
            customVisualProps.chartSettingsVersion;
    }
    return sanitizedVisualProps;
};
//# sourceMappingURL=helpers.js.map